/**
 * PERCHE' UN FILE NON SI E' LETTO, IN UNA PAROLA (STU-424)
 *
 * Il 23/09 una studentessa ha scelto un video di 17 MB girato col telefono.
 * Il server lo ha trascritto, non ci ha trovato parlato e ha risposto 422.
 * Lei non l'ha mai saputo: aveva gia' lasciato la Home, e anche restando
 * avrebbe letto "Nessun testo estratto" e un errore tecnico non tradotto.
 * Dal 15/09, 17 scelte di file su 35 perse erano letture fallite cosi', in
 * silenzio: audio o video senza parlato, un `.pages`, PDF da 41-54 MB
 * caduti su Storage o con un 400.
 *
 * Qui l'errore diventa un CODICE, uno solo per tutti: l'avviso che parte dal
 * router action, la riga del file nella card e il campo `reason`
 * dell'evento GA4 `document_pipeline_failed` leggono lo stesso. Ogni codice
 * ha in upload.json (`readErrors`) il testo lungo con il rimedio e quello
 * breve per la card.
 *
 * Funzioni pure: niente i18n, niente rete, niente Redux. I messaggi del
 * server sono in italiano (server/handlers/content/media/trascrizione.py,
 * server.py), quelli del browser anche in inglese e spagnolo (errors.json,
 * file.unsupportedFormat): le regole li riconoscono tutti.
 */
import { mimePulito } from '@config/uploadFormats';

export const MOTIVI = Object.freeze({
  NESSUN_PARLATO: 'nessun_parlato',
  TESTO_SCARSO: 'testo_scarso',
  FORMATO_NON_SUPPORTATO: 'formato_non_supportato',
  TROPPO_GRANDE: 'troppo_grande',
  CONNESSIONE: 'connessione',
  ERRORE_SERVER: 'errore_server',
  GENERICO: 'generico',
});

const TUTTI_I_MOTIVI = new Set(Object.values(MOTIVI));

// "Nessun parlato riconosciuto nella registrazione" e' la frase del server
// quando il modello risponde e risponde vuoto (trascrizione.py).
const NESSUN_PARLATO = /nessun parlato|no speech/i;

// Il formato: i tre testi di errors.json (file.unsupportedFormat) e i due
// del server (400 "Tipo di file non ammesso", 422 "Formato non gestito").
const FORMATO = /formato non supportato|unsupported format|formato no soportado|tipo di file non ammesso|formato non gestito/i;

// La taglia, quando qualcuno la dice: il server ("File troppo grande: ...",
// "Troppe immagini"), il browser, i proxy davanti a Cloud Run.
const TROPPO_GRANDE = /troppo grande|too large|payload too large|request entity too large|troppe immagini|too many images/i;

// Storage si usa solo sopra i 20 MiB (services/condottoLargo.js): un suo
// errore, nel codice o nel testo ("Firebase Storage: ... (storage/...)"),
// e' un errore di un file grande. Tranne quando Storage smette di riprovare
// (sotto, STORAGE_SI_ARRENDE): li' il file non c'entra, e' la rete.
const STORAGE = /storage\//i;

// La rete che cede prima che arrivi una risposta, nelle forme in cui arriva
// qui: il testo di axios ("Network Error", "Request aborted"), quello di
// fetch in ogni browser ("Failed to fetch" su Chrome, "Load failed" su
// Safari, "NetworkError when attempting to fetch resource." su Firefox), il
// codice di axios (ECONNABORTED qui arriva solo dalla trascrizione, che non
// ha un tempo massimo: e' una richiesta interrotta). E Storage, che dopo i
// suoi tentativi si arrende con `storage/retry-limit-exceeded`, nel codice e
// in fondo al messaggio. Allo studente va detto di riprovare con una rete
// migliore: prima gli dicevamo di dividere il file, o che il problema era
// nostro (STU-424).
const RETE = /network ?error|failed to fetch|\bload failed|request aborted/i;
const CODICI_DI_RETE = /ECONNABORTED|ETIMEDOUT|ERR_NETWORK/;
const STORAGE_SI_ARRENDE = /storage\/retry-limit-exceeded/i;

// Guasti nostri, che lo studente non puo' correggere: gli dobbiamo dire di
// riprovare, non di cambiare file. Anche il tempo scaduto sta qui: nei nostri
// messaggi e' l'elaborazione ad averci messo troppo, non la rete.
const GUASTO = /trascrizione non riuscita|normalizzazione fallita|client openai|il taglio non ha prodotto|internal server error|bad gateway|service unavailable|gateway time-?out|timeout|timed out|troppo tempo/i;

// Un testo vuoto o troppo corto. Su un audio o un video vuol dire che di
// parlato non ce n'era abbastanza; su tutto il resto che nel file c'e' poco
// da studiare (pagine scansionate, un documento corto). In nessuno dei due
// casi riprovare cambia qualcosa.
const TESTO_VUOTO = /nessun testo|testo estratto (e|è) vuoto|non contiene testo|maggiore di 300 caratteri|più testo/i;

// Le forme in cui il codice HTTP finisce dentro il messaggio quando nessuno
// lo porta a parte: axios, extractTextFromPdf, estraiViaStorage.
const STATUS_NEL_MESSAGGIO = [/status code (\d{3})/i, /errore server:? (\d{3})/i, /\((\d{3})\)/];

/** Il codice HTTP scritto dentro un messaggio d'errore, o null. */
export const statusDalMessaggio = (messaggio) => {
  const testo = String(messaggio || '');
  for (const forma of STATUS_NEL_MESSAGGIO) {
    const trovato = forma.exec(testo);
    if (trovato) return Number(trovato[1]);
  }
  return null;
};

const eAudioOVideo = (tipo) => {
  const mime = mimePulito(tipo);
  return mime.startsWith('audio/') || mime.startsWith('video/');
};

/**
 * Il codice di un fallimento, dai fatti che se ne sanno.
 *
 * @param {Object} fatti
 * @param {string} [fatti.messaggio] il testo dell'errore (quello del server, se c'e')
 * @param {number} [fatti.status] il codice HTTP, se noto
 * @param {string} [fatti.codice] il codice dell'errore (es. `storage/retry-limit-exceeded`)
 * @param {string} [fatti.tipo] il mime del file
 * @param {boolean} [fatti.formatoNonSupportato] vero se processFile non ha nemmeno provato
 * @returns {string} uno dei MOTIVI
 */
export const motivoErroreEstrazione = ({
  messaggio = '',
  status = null,
  codice = null,
  tipo = '',
  formatoNonSupportato = false,
} = {}) => {
  const testo = typeof messaggio === 'string' ? messaggio : String(messaggio ?? '');
  const sigla = typeof codice === 'string' ? codice : '';
  const http = Number.isInteger(status) ? status : statusDalMessaggio(testo);

  if (formatoNonSupportato || FORMATO.test(testo)) return MOTIVI.FORMATO_NON_SUPPORTATO;
  if (NESSUN_PARLATO.test(testo)) return MOTIVI.NESSUN_PARLATO;
  // Prima della taglia: il retry-limit e' un errore di Storage, e la regola
  // della taglia lo prenderebbe per un file troppo pesante.
  if (
    RETE.test(testo) || CODICI_DI_RETE.test(sigla)
    || STORAGE_SI_ARRENDE.test(sigla) || STORAGE_SI_ARRENDE.test(testo)
  ) {
    return MOTIVI.CONNESSIONE;
  }
  if (http === 413 || TROPPO_GRANDE.test(testo) || STORAGE.test(sigla) || STORAGE.test(testo)) {
    return MOTIVI.TROPPO_GRANDE;
  }
  if ((http && http >= 500) || GUASTO.test(testo)) {
    return MOTIVI.ERRORE_SERVER;
  }
  // Un 422 su un audio o un video, senza un guasto dichiarato, e' il server
  // che ha trascritto e non ha trovato voce: e' il caso del 23/09 quando il
  // messaggio del server si e' perso per strada.
  if (eAudioOVideo(tipo) && (http === 422 || !testo || TESTO_VUOTO.test(testo))) {
    return MOTIVI.NESSUN_PARLATO;
  }
  // Un PDF, un Word, una presentazione o una foto con troppo poco testo: il
  // documento sotto i 300 caratteri, "Il PDF non contiene testo leggibile".
  // Prima finiva in `generico`, che dice di riprovare: stesso file, stesso
  // esito (STU-424).
  if (!eAudioOVideo(tipo) && TESTO_VUOTO.test(testo)) {
    return MOTIVI.TESTO_SCARSO;
  }
  return MOTIVI.GENERICO;
};

/**
 * Il codice di un errore cosi' come arriva al router action: l'esito di
 * processFile (`{ errorMessage, status, codice, formatoNonSupportato }`),
 * un Error, un errore di axios o una stringa.
 */
export const motivoDaErrore = (errore, file = null) => {
  const messaggio = typeof errore === 'string'
    ? errore
    : errore?.errorMessage || errore?.response?.data?.detail || errore?.message || '';
  const status = errore?.status ?? errore?.response?.status ?? null;
  return motivoErroreEstrazione({
    messaggio: typeof messaggio === 'string' ? messaggio : '',
    status: Number.isInteger(status) ? status : null,
    codice: errore?.codice || errore?.code || null,
    tipo: file?.type || errore?.type || '',
    formatoNonSupportato: Boolean(errore?.formatoNonSupportato),
  });
};

/** Un codice che non conosciamo (stato vecchio, refuso) vale `generico`. */
export const motivoNoto = (motivo) => (TUTTI_I_MOTIVI.has(motivo) ? motivo : MOTIVI.GENERICO);

/** La chiave i18n (namespace `upload`) del testo lungo: cosa e' successo e cosa fare. */
export const chiaveAvviso = (motivo) => `readErrors.toast.${motivoNoto(motivo)}`;

/** La chiave i18n (namespace `upload`) del motivo breve, per la riga del file. */
export const chiaveMotivoBreve = (motivo) => `readErrors.short.${motivoNoto(motivo)}`;

/** Vero per l'elemento della coda la cui lettura e' fallita. */
export const eFallito = (file) => Boolean(file?.estrazioneFallita);

/** Solo i file che si possono mandare a Genera. */
export const fileLeggibili = (files) => (Array.isArray(files) ? files.filter((file) => !eFallito(file)) : []);

/**
 * Il motivo di una scelta scartata perche' il piano gratuito e' pieno. Non e'
 * un errore di lettura, per questo non sta in MOTIVI: le scelte scartate per
 * taglia o formato usano invece gli stessi codici delle letture fallite.
 */
export const LIMITE_PIANO = 'limite_piano';

/** Il nome dell'evento GA4 di una scelta scartata prima del caricamento. */
export const EVENTO_SCELTA_SCARTATA = 'document_selection_rejected';

const estensione = (nome) => {
  const trovata = /\.([a-z0-9]+)$/i.exec(String(nome || ''));
  return trovata ? `.${trovata[1].toLowerCase()}` : 'unknown';
};

const megabyte = (byte) => (Number.isFinite(byte) ? parseFloat((byte / (1024 * 1024)).toFixed(2)) : 0);

/**
 * Il payload di `document_selection_rejected`: una scelta di file scartata
 * PRIMA del caricamento (taglia, limite del piano, formato che si incolla a
 * mano). `document_selected_to_upload` resta dov'e': le dashboard ci
 * contano sopra, e questo evento serve a sottrarre le scelte che non sono
 * mai partite.
 */
export const payloadSceltaScartata = ({
  file = null,
  motivo,
  quanti = 1,
  origine = 'file_picker',
  tettoMb = null,
} = {}) => {
  const payload = {
    category: 'document',
    reason: motivo,
    files_count: quanti,
    file_size_mb: megabyte(file?.size),
    file_extension: estensione(file?.name),
    mime_type: file?.type || 'unknown',
    upload_source: origine,
  };
  if (Number.isFinite(tettoMb)) payload.limit_mb = tettoMb;
  return payload;
};
